import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Calculator, ChevronDown, Lightbulb, Loader2, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input, Select } from '@/components/ui/input'
import { getAttributeValues } from '@/features/materials/attribute-values-api'
import { usePrices } from '@/features/prices/usePrices'
import { kotlinToDoubleOrNull } from '@/features/rules/logic-validate'
import {
  buildFacts,
  buildSimulationContext,
  EMPTY_ANSWERS,
  hasFieldSchema,
  priceQuantities,
  simulate,
  type SimulationAnswers,
  type SimulationConfig,
  type SimulationContext,
  type TraceRow,
} from '@/features/rules/simulate'
import { ANSWER_TYPE_LABELS, COST_TYPE_LABELS, CURRENCY_SYMBOLS } from '@/lib/constants'
import type { ConfigQuestion, Currency, Material, QuestionOption } from '@/lib/types'
import { cn } from '@/lib/utils'

const formatNumber = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString('ru-RU', { maximumFractionDigits: 4 }) : String(value)

const formatMoney = (value: number, currency: Currency | null) =>
  `${value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${
    currency ? ` ${CURRENCY_SYMBOLS[currency]}` : ''
  }`

const materialLabel = (material: Material) =>
  `${material.name}${material.isActive ? '' : ' (отключён)'}${material.isPrivate ? ' · личный' : ''}`

/**
 * "Симулировать калькуляцию" for one section type: the room's questions (as the app shows them, with
 * display conditions applied) and, next to them, what the app's automated calculation would produce
 * for ONE room of this type. Answers live only in this component's state — closing the dialog drops
 * them; nothing is written anywhere. The engine is `simulate.ts`.
 */
export function SimulationDialog({
  uid,
  estateName,
  config,
  onClose,
}: {
  uid: string | undefined
  estateName: string
  config: SimulationConfig
  onClose: () => void
}) {
  const ctx = useMemo(() => buildSimulationContext(config), [config])
  const [answers, setAnswers] = useState<SimulationAnswers>(EMPTY_ANSWERS)
  const [showHidden, setShowHidden] = useState(false)
  const facts = useMemo(() => buildFacts(answers, ctx.singleEntryKeys), [answers, ctx.singleEntryKeys])
  const attributes = useAttributeCache(ctx, facts)
  const result = useMemo(
    () => simulate(ctx, facts, attributes.values),
    [ctx, facts, attributes.values],
  )
  const { prices, isLoading: pricesLoading, error: pricesError } = usePrices(uid)
  const estimate = useMemo(
    () => priceQuantities(result.quantities, ctx.materialById, prices),
    [result.quantities, ctx.materialById, prices],
  )

  const hiddenCount = result.questions.filter((item) => !item.visible).length
  const shown = result.questions.filter((item) => item.visible || showHidden)

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-[min(96vw,80rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="size-5" />
            Симуляция калькуляции: {config.sectionType.name}
          </DialogTitle>
          <DialogDescription>
            {estateName ? `${estateName} › ${config.sectionType.name}. ` : ''}Одно помещение этого типа,
            вопросы и расчёт — как в приложении. Ответы и результат нигде не сохраняются и пропадут
            при закрытии окна.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)]">
          <div className="flex min-w-0 flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-muted-foreground flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(event) => setShowHidden(event.target.checked)}
                />
                Показывать скрытые условием ({hiddenCount})
              </label>
              <Button variant="outline" size="sm" onClick={() => setAnswers(EMPTY_ANSWERS)}>
                <RotateCcw />
                Сбросить ответы
              </Button>
            </div>
            {result.questions.length === 0 ? (
              <p className="text-muted-foreground py-6 text-center text-sm">
                У этого раздела нет вопросов — ни своих, ни из групп.
              </p>
            ) : null}
            {shown.map((item, index) => {
              const previous = shown[index - 1]
              const header =
                index === 0 || previous?.question.groupId !== item.question.groupId ? (
                  <h3 className="text-muted-foreground mt-2 text-xs font-medium tracking-wide uppercase">
                    {item.question.groupId
                      ? `Группа «${ctx.groupById.get(item.question.groupId)?.name ?? item.question.groupId}»`
                      : 'Вопросы раздела'}
                  </h3>
                ) : null
              return (
                <div key={item.question.id} className="flex flex-col gap-2">
                  {header}
                  <QuestionCard
                    ctx={ctx}
                    question={item.question}
                    visible={item.visible}
                    conditionError={item.conditionError}
                    answers={answers}
                    onChange={setAnswers}
                  />
                </div>
              )
            })}
          </div>

          <div className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-0 lg:self-start">
            <ResultPanel
              estimate={estimate}
              pricesLoading={pricesLoading}
              pricesError={pricesError}
              attributesLoading={attributes.loading}
              attributesError={attributes.error}
            />
            {result.hints.length > 0 || result.hintErrors.length > 0 ? (
              <section className="flex flex-col gap-2">
                <h3 className="text-sm font-medium">Подсказки</h3>
                {result.hints.map((hint) => (
                  <div
                    key={hint.id}
                    className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-sm"
                  >
                    <Lightbulb className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    <span>{hint.text || '(без текста)'}</span>
                  </div>
                ))}
                {result.hintErrors.map(({ hint, error }) => (
                  <p key={hint.id} className="text-destructive text-xs">
                    Подсказка «{hint.text || hint.id}» не покажется — ошибка в условии: {error}
                  </p>
                ))}
                {result.hints.length > 1 ? (
                  <p className="text-muted-foreground text-xs">
                    В приложении подсказки всплывают по одной, в этом порядке.
                  </p>
                ) : null}
              </section>
            ) : null}
            <TracePanel ctx={ctx} trace={result.trace} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Attribute values (`{attr:CODE}`) of the materials the calculation may read, fetched per material on
 * first use and kept for the life of the dialog (one small query per picked material).
 */
function useAttributeCache(ctx: SimulationContext, facts: Record<string, string>) {
  const [state, setState] = useState<{
    values: Map<string, Record<string, string>>
    requested: ReadonlySet<string>
    error: string | null
  }>({ values: new Map(), requested: new Set(), error: null })
  // Which materials are needed does not depend on attribute values, so an empty map is enough here.
  const neededKey = useMemo(
    () => simulate(ctx, facts, new Map()).attributeMaterialIds.join('\n'),
    [ctx, facts],
  )

  useEffect(() => {
    const missing = neededKey
      ? neededKey.split('\n').filter((id) => !state.requested.has(id))
      : []
    if (missing.length === 0) return
    let active = true
    const requested = new Set(state.requested)
    for (const id of missing) requested.add(id)
    Promise.all(
      missing.map(async (id) => {
        const values = await getAttributeValues(id)
        const byCode: Record<string, string> = {}
        // `associate { attributeCode to value }`: a later duplicate code wins.
        for (const value of values) byCode[value.attributeCode] = value.value
        return [id, byCode] as const
      }),
    )
      .then((entries) => {
        if (!active) return
        setState((current) => {
          const next = new Map(current.values)
          for (const [id, byCode] of entries) next.set(id, byCode)
          return { values: next, requested: new Set([...current.requested, ...requested]), error: null }
        })
      })
      .catch((caught: unknown) => {
        if (!active) return
        setState((current) => ({
          ...current,
          requested: new Set([...current.requested, ...requested]),
          error: caught instanceof Error ? caught.message : String(caught),
        }))
      })
    return () => {
      active = false
    }
  }, [neededKey, state.requested])

  const loading = neededKey !== '' && neededKey.split('\n').some((id) => !state.values.has(id)) && !state.error
  return { values: state.values, loading, error: state.error }
}

function ResultPanel({
  estimate,
  pricesLoading,
  pricesError,
  attributesLoading,
  attributesError,
}: {
  estimate: ReturnType<typeof priceQuantities>
  pricesLoading: boolean
  pricesError: string | null
  attributesLoading: boolean
  attributesError: string | null
}) {
  const currency = estimate.currencies.length === 1 ? (estimate.currencies[0] ?? null) : null
  return (
    <section className="flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Результат калькуляции</h3>
        {attributesLoading ? (
          <span className="text-muted-foreground flex items-center gap-1 text-xs">
            <Loader2 className="size-3 animate-spin" />
            атрибуты пунктов…
          </span>
        ) : null}
      </div>
      {pricesError ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Цены не загружены</AlertTitle>
          <AlertDescription>{pricesError}</AlertDescription>
        </Alert>
      ) : null}
      {attributesError ? (
        <p className="text-destructive text-xs">
          Атрибуты пунктов не загружены — {'{attr:…}'} считаются пустыми: {attributesError}
        </p>
      ) : null}
      {estimate.lines.length === 0 ? (
        <p className="text-muted-foreground py-4 text-center text-sm">
          Пока ничего не начислено — ответьте на вопросы слева.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b text-left text-xs">
                <th className="py-1 pr-2 font-medium">Пункт</th>
                <th className="py-1 pr-2 text-right font-medium">Кол-во</th>
                <th className="py-1 pr-2 text-right font-medium">Цена</th>
                <th className="py-1 text-right font-medium">Сумма</th>
              </tr>
            </thead>
            <tbody>
              {estimate.lines.map((line) => (
                <tr key={line.materialId} className="border-b align-top last:border-0">
                  <td className="py-1.5 pr-2">
                    <div className={cn(line.isUnknown && 'text-destructive')}>{line.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {COST_TYPE_LABELS[line.costType]}
                      {line.isUnknown ? ' · пункта нет в каталоге' : ''}
                    </div>
                  </td>
                  <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                    {formatNumber(line.quantity)} {line.unit}
                  </td>
                  <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                    {line.hasPrice ? (
                      formatMoney(line.unitPrice, line.currency)
                    ) : (
                      <span className="text-amber-600">{pricesLoading ? '…' : 'нет цены'}</span>
                    )}
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    {formatMoney(line.lineTotal, line.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {estimate.lines.length > 0 ? (
        <div className="flex flex-col gap-1 border-t pt-2 text-sm">
          {estimate.subtotals.length > 1
            ? estimate.subtotals.map((item) => (
                <div key={item.costType} className="text-muted-foreground flex justify-between">
                  <span>{COST_TYPE_LABELS[item.costType]}</span>
                  <span>{formatMoney(item.amount, currency)}</span>
                </div>
              ))
            : null}
          <div className="flex justify-between font-semibold">
            <span>Итого по помещению</span>
            <span>{formatMoney(estimate.total, currency)}</span>
          </div>
          {estimate.currencies.length > 1 ? (
            <p className="text-xs text-amber-600">
              Цены в разных валютах ({estimate.currencies.join(', ')}): приложение складывает суммы без
              пересчёта, как и здесь.
            </p>
          ) : null}
          <p className="text-muted-foreground text-xs">
            По ценам каталога. Работа, скидка и налог задаются в проекте и здесь не учитываются.
          </p>
        </div>
      ) : null}
    </section>
  )
}

/** Every effect evaluation: which question, entry and effect, the value, and why it did not count. */
function TracePanel({ ctx, trace }: { ctx: SimulationContext; trace: TraceRow[] }) {
  const [open, setOpen] = useState(false)
  const errors = trace.filter((row) => row.error).length
  if (trace.length === 0) return null
  const questionById = new Map(ctx.questions.map((question) => [question.id, question]))
  return (
    <section className="rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="hover:bg-accent/50 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm"
      >
        <span className="flex items-center gap-2 font-medium">
          Как посчитано
          <Badge variant="outline">{trace.length}</Badge>
          {errors > 0 ? <Badge variant="destructive">ошибок: {errors}</Badge> : null}
        </span>
        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="flex flex-col gap-1.5 border-t p-3">
          {trace.map((row, index) => {
            const question = questionById.get(row.questionId)
            const material = ctx.materialById.get(row.target)
            // An effect that adds a fixed cost item together with the picked material.
            const isFixedCompanion =
              row.effectId !== null &&
              question !== undefined &&
              (question.answerType === 'CHOICE' ||
                question.answerType === 'MULTI_CHOICE' ||
                hasFieldSchema(question)) &&
              (ctx.effectsByQuestion.get(row.questionId) ?? []).some(
                (effect) => effect.id === row.effectId && effect.fixedMaterial,
              )
            return (
              <div key={index} className="rounded-md border p-2 text-xs">
                <div className="font-medium">
                  {question?.prompt || question?.factKey || row.questionId}
                  {row.entryNumber !== null ? ` · запись ${row.entryNumber}` : ''}
                  {row.effectNumber !== null ? ` · эффект №${row.effectNumber}` : ' · без эффекта'}
                  {isFixedCompanion ? ' (с пунктом затрат)' : ''}
                </div>
                <div className="text-muted-foreground">
                  → {row.target ? (material?.name ?? row.target) : 'пункт не определён'}
                </div>
                <div className={cn(row.skipped ? 'text-muted-foreground' : 'text-foreground')}>
                  = {formatNumber(row.value)}
                  {row.multiplier !== null ? ` × ${formatNumber(row.multiplier)}` : ''}
                  {row.skipped ? ` — не учтено: ${row.skipped}` : ''}
                </div>
                {row.error ? <div className="text-destructive">Ошибка выражения: {row.error} (считается 0)</div> : null}
              </div>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}

// ---- Answer inputs (one per answer type, as `RoomScreen.AnswerCard`) ----

type CardProps = {
  ctx: SimulationContext
  question: ConfigQuestion
  visible: boolean
  conditionError: string | null
  answers: SimulationAnswers
  onChange: (update: (current: SimulationAnswers) => SimulationAnswers) => void
}

function QuestionCard({ ctx, question, visible, conditionError, answers, onChange }: CardProps) {
  const options = ctx.optionsByQuestion.get(question.id) ?? []
  const effectCount = ctx.effectsByQuestion.get(question.id)?.length ?? 0
  return (
    <div className={cn('flex flex-col gap-2 rounded-lg border p-3', !visible && 'bg-muted/40 opacity-60')}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium">{question.prompt || question.factKey || '(без текста)'}</div>
          <div className="text-muted-foreground text-xs">
            {ANSWER_TYPE_LABELS[question.answerType]} ·{' '}
            {question.answerType === 'FACT' ? (
              'без ключа'
            ) : (
              <code className="font-mono">{`{${question.factKey || '—'}}`}</code>
            )}
            {question.unit ? ` · ${question.unit}` : ''}
            {effectCount > 0 ? ` · эффектов: ${effectCount}` : ''}
          </div>
        </div>
        {!visible ? <Badge variant="outline">скрыт условием</Badge> : null}
      </div>
      {question.displayCondition.trim() ? (
        <code className="text-muted-foreground font-mono text-xs break-all">
          условие: {question.displayCondition}
        </code>
      ) : null}
      {conditionError ? (
        <p className="text-xs text-amber-600">
          Ошибка в условии — вопрос показывается всегда: {conditionError}
        </p>
      ) : null}
      <fieldset disabled={!visible} className="min-w-0">
        <AnswerInput
          ctx={ctx}
          question={question}
          options={options}
          hasEffects={effectCount > 0}
          answers={answers}
          onChange={onChange}
        />
      </fieldset>
    </div>
  )
}

function AnswerInput({
  ctx,
  question,
  options,
  hasEffects,
  answers,
  onChange,
}: {
  ctx: SimulationContext
  question: ConfigQuestion
  options: QuestionOption[]
  hasEffects: boolean
  answers: SimulationAnswers
  onChange: CardProps['onChange']
}) {
  const key = question.factKey
  const scalar = answers.scalar[key] ?? ''
  const setScalar = (value: string) =>
    onChange((current) => ({ ...current, scalar: { ...current.scalar, [key]: value } }))

  switch (question.answerType) {
    case 'NUMBER':
      return (
        <div className="flex flex-col gap-1">
          <Input
            inputMode="decimal"
            value={scalar}
            onChange={(event) => setScalar(event.target.value)}
            aria-label={question.prompt || key}
          />
          {scalar.trim() && kotlinToDoubleOrNull(scalar.trim()) === null ? (
            <span className="text-xs text-amber-600">
              Не число — формулы с этим ключом дадут ошибку (дробь пишется через точку).
            </span>
          ) : null}
        </div>
      )
    case 'TEXT':
      return (
        <Input
          value={scalar}
          onChange={(event) => setScalar(event.target.value)}
          aria-label={question.prompt || key}
        />
      )
    case 'BOOLEAN':
      return (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={scalar.toLowerCase() === 'true'}
            onChange={(event) => setScalar(event.target.checked ? 'true' : 'false')}
          />
          {scalar.toLowerCase() === 'true' ? 'Да' : 'Нет'}
          {scalar === '' ? <span className="text-muted-foreground text-xs">(не отвечено)</span> : null}
        </label>
      )
    case 'CHOICE':
      return (
        <MaterialSelect
          materials={ctx.materialsOf(question.materialCategoryId, question.materialCategory)}
          category={ctx.categoryName(question.materialCategoryId, question.materialCategory)}
          value={scalar}
          onChange={setScalar}
          label={question.prompt || key}
        />
      )
    case 'SELECT':
      return options.length === 0 ? (
        <Empty text="У списка нет вариантов." />
      ) : (
        <Select value={scalar} onChange={(event) => setScalar(event.target.value)} aria-label={question.prompt || key}>
          <option value="">— выберите —</option>
          {options.map((option) => (
            <option key={option.id} value={option.value}>
              {option.label || option.value}
            </option>
          ))}
        </Select>
      )
    case 'KEY_SET':
      return options.length === 0 ? (
        <Empty text="В наборе нет элементов." />
      ) : (
        <div className="flex flex-col gap-1">
          {options.map((option) => (
            <label key={option.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={answers.keySet[option.value.trim()] === true}
                onChange={(event) =>
                  onChange((current) => ({
                    ...current,
                    keySet: { ...current.keySet, [option.value.trim()]: event.target.checked },
                  }))
                }
              />
              {option.label || option.value}
              <code className="text-muted-foreground font-mono text-xs">{`{${option.value}}`}</code>
            </label>
          ))}
        </div>
      )
    case 'MULTI_CHOICE':
      return (
        <MultiChoiceInput
          materials={ctx.materialsOf(question.materialCategoryId, question.materialCategory)}
          category={ctx.categoryName(question.materialCategoryId, question.materialCategory)}
          // With an effect the quantity comes from the formula: the app hides the count (= 1).
          showCount={!hasEffects}
          selections={answers.multi[key] ?? {}}
          onChange={(selections) =>
            onChange((current) => ({ ...current, multi: { ...current.multi, [key]: selections } }))
          }
        />
      )
    case 'REPEATER':
    case 'QUESTION_LIST':
      return (
        <RepeaterInput
          ctx={ctx}
          fields={options}
          single={question.answerType === 'QUESTION_LIST'}
          entries={answers.repeater[key] ?? []}
          onChange={(entries) =>
            onChange((current) => ({ ...current, repeater: { ...current.repeater, [key]: entries } }))
          }
        />
      )
    case 'FACT':
      // Not shown in the app's interview: nothing to answer, its effects are added automatically.
      return (
        <Empty
          text={
            hasEffects
              ? 'В приложении не показывается: эффекты добавляются автоматически.'
              : 'В приложении не показывается. Эффектов нет — ничего не добавит.'
          }
        />
      )
  }
}

function Empty({ text }: { text: string }) {
  return <p className="text-muted-foreground text-xs">{text}</p>
}

function MaterialSelect({
  materials,
  category,
  value,
  onChange,
  label,
}: {
  materials: Material[]
  category: string
  value: string
  onChange: (value: string) => void
  label: string
}) {
  if (materials.length === 0) {
    return <Empty text={category.trim() ? `В категории «${category}» нет пунктов.` : 'Категория не задана.'} />
  }
  return (
    <Select value={value} onChange={(event) => onChange(event.target.value)} aria-label={label}>
      <option value="">— выберите —</option>
      {materials.map((material) => (
        <option key={material.id} value={material.id}>
          {materialLabel(material)}
        </option>
      ))}
    </Select>
  )
}

function MultiChoiceInput({
  materials,
  category,
  showCount,
  selections,
  onChange,
}: {
  materials: Material[]
  category: string
  showCount: boolean
  selections: Record<string, string>
  onChange: (selections: Record<string, string>) => void
}) {
  if (materials.length === 0) {
    return <Empty text={category.trim() ? `В категории «${category}» нет пунктов.` : 'Категория не задана.'} />
  }
  return (
    <div className="flex flex-col gap-1">
      {materials.map((material) => {
        const selected = Object.prototype.hasOwnProperty.call(selections, material.id)
        return (
          <div key={material.id} className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex min-w-0 flex-1 items-center gap-2">
              <input
                type="checkbox"
                checked={selected}
                onChange={(event) => {
                  const next = { ...selections }
                  if (event.target.checked) next[material.id] = '1'
                  else delete next[material.id]
                  onChange(next)
                }}
              />
              <span className="truncate">{materialLabel(material)}</span>
            </label>
            {selected && showCount ? (
              <Input
                inputMode="decimal"
                className="h-8 w-24"
                value={selections[material.id] ?? ''}
                onChange={(event) => onChange({ ...selections, [material.id]: event.target.value })}
                aria-label={`Количество: ${material.name}`}
              />
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

function RepeaterInput({
  ctx,
  fields,
  single,
  entries,
  onChange,
}: {
  ctx: SimulationContext
  fields: QuestionOption[]
  single: boolean
  entries: Array<Record<string, string>>
  onChange: (entries: Array<Record<string, string>>) => void
}) {
  if (fields.length === 0) return <Empty text="У вопроса нет полей." />
  // QUESTION_LIST: exactly one entry is always open.
  const shown = single ? [entries[0] ?? {}] : entries
  const update = (index: number, fieldKey: string, value: string) =>
    onChange(shown.map((entry, position) => (position === index ? { ...entry, [fieldKey]: value } : entry)))
  return (
    <div className="flex flex-col gap-2">
      {shown.map((entry, index) => (
        <div key={index} className="flex flex-col gap-2 rounded-md border p-2">
          {!single ? (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium">Запись {index + 1}</span>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive size-7"
                onClick={() => onChange(shown.filter((_, position) => position !== index))}
                aria-label={`Удалить запись ${index + 1}`}
              >
                <Trash2 />
              </Button>
            </div>
          ) : null}
          {fields.map((field) => {
            const label = field.label || field.value
            const value = entry[field.value] ?? ''
            return (
              <div key={field.id} className="flex flex-col gap-1">
                {field.fieldType === 'BOOLEAN' ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={value.toLowerCase() === 'true'}
                      onChange={(event) => update(index, field.value, event.target.checked ? 'true' : 'false')}
                    />
                    {label}
                  </label>
                ) : (
                  <>
                    <span className="text-muted-foreground text-xs">
                      {label} <code className="font-mono">{`{${field.value}}`}</code>
                    </span>
                    {field.fieldType === 'CHOICE' ? (
                      <MaterialSelect
                        materials={ctx.materialsOf(field.materialCategoryId, field.materialCategory)}
                        category={ctx.categoryName(field.materialCategoryId, field.materialCategory)}
                        value={value}
                        onChange={(next) => update(index, field.value, next)}
                        label={label}
                      />
                    ) : (
                      <Input
                        inputMode={field.fieldType === 'NUMBER' ? 'decimal' : undefined}
                        value={value}
                        onChange={(event) => update(index, field.value, event.target.value)}
                        aria-label={label}
                      />
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      ))}
      {!single ? (
        <Button variant="outline" size="sm" className="w-fit" onClick={() => onChange([...entries, {}])}>
          <Plus />
          Добавить запись
        </Button>
      ) : null}
    </div>
  )
}
